import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CaretDown, CaretUp, Copy, DownloadSimple, EnvelopeSimple } from "@phosphor-icons/react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { FieldHint, FieldLabel } from "../shared/FieldHint";
import { formatET } from "../../lib/dateUtils";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useEditConfirm } from "../../context/EditConfirmContext";

export function ManagerCard({ manager, onRateUpdated, highlighted }) {
  const confirmEdit = useEditConfirm();
  const [rateDraft, setRateDraft] = useState(manager.commission.override_rate);
  const [emailDraft, setEmailDraft] = useState(manager.email);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [teamPerformance, setTeamPerformance] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [showDownline, setShowDownline] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [showEmailEdit, setShowEmailEdit] = useState(false);

  useEffect(() => {
    setRateDraft(manager.commission.override_rate);
    setEmailDraft(manager.email);
  }, [manager.commission.override_rate, manager.email]);

  const saveRate = async () => {
    if (!(await confirmEdit(`${manager.display_name}'s override rate`))) return;
    try {
      await api.patch(`/admin/managers/${manager.id}/override-rate`, { override_commission_rate: parseFloat(rateDraft) || 0 });
      toast.success("Override rate updated");
      onRateUpdated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveEmail = async () => {
    if (!(await confirmEdit(`${manager.display_name}'s email`))) return;
    try {
      const { data } = await api.patch(`/admin/managers/${manager.id}/email`, { email: emailDraft });
      toast.success(data.message);
      setShowEmailEdit(false);
      onRateUpdated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const togglePayment = async () => {
    if (showPayment) { setShowPayment(false); return; }
    try {
      const { data } = await api.get(`/admin/managers/${manager.id}/payment-info`);
      setPaymentInfo(data);
      setShowPayment(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No payment info on file yet");
    }
  };

  const toggleAgreement = async () => {
    if (showAgreement) { setShowAgreement(false); return; }
    try {
      const { data } = await api.get(`/admin/managers/${manager.id}/agreement`);
      setAgreement(data);
      setShowAgreement(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load agreement status");
    }
  };

  const toggleDownline = async () => {
    if (showDownline) { setShowDownline(false); return; }
    setShowDownline(true);
    try {
      const { data } = await api.get(`/admin/managers/${manager.id}/team-performance`);
      setTeamPerformance(data);
    } catch {
      // fall back to the plain roster list below if this fails
    }
  };

  const downloadPdf = async (url, filename) => {
    try {
      const response = await api.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("Couldn't download PDF");
    }
  };

  const downloadAgreementPdf = () => downloadPdf(`/admin/managers/${manager.id}/agreement/pdf`, `${manager.code}_agreement.pdf`);
  const downloadPaymentInfoPdf = () => downloadPdf(`/admin/managers/${manager.id}/payment-info/pdf`, `${manager.code}_payment_tax_info.pdf`);

  const emailAgreementPdf = async () => {
    try {
      const { data } = await api.post(`/admin/managers/${manager.id}/agreement/email`);
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't email agreement PDF");
    }
  };

  const emailPaymentInfoPdf = async () => {
    try {
      const { data } = await api.post(`/admin/managers/${manager.id}/payment-info/email`);
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't email payment info PDF");
    }
  };

  const sendWelcomeEmail = async () => {
    try {
      const { data } = await api.post(`/admin/managers/${manager.id}/send-welcome-email`);
      toast.success(data.message);
      onRateUpdated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const signupLink = `${window.location.origin}/join?ref=${manager.code}`;
  const copySignupLink = () => {
    navigator.clipboard.writeText(signupLink).then(() => toast.success("Signup link copied")).catch(() => toast.error("Couldn't copy"));
  };

  return (
    <div id={`manager-card-${manager.id}`} className={`bg-igc-surface border p-5 transition-colors ${highlighted ? "border-igc-gold ring-2 ring-igc-gold/40" : "border-igc-gold/15"}`} data-testid={`management-team-manager-card-${manager.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="font-serif text-lg text-igc-cream"><span className="text-igc-goldText">{manager.code}</span> — {manager.display_name}</p>
          {showEmailEdit ? (
            <div className="flex items-center gap-2 mt-1">
              <Input data-testid={`management-team-email-input-${manager.id}`} value={emailDraft} onChange={(e) => setEmailDraft(e.target.value)} title="Edit this manager's login/contact email." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-56 h-7 text-xs" />
              <Button data-testid={`management-team-email-save-${manager.id}`} onClick={saveEmail} title="Saves the new email address for this manager." className="h-7 text-[11px] bg-transparent border border-igc-gold text-igc-goldText hover:bg-igc-gold hover:text-black">Save</Button>
            </div>
          ) : (
            <p className="text-igc-muted text-xs">
              {manager.email}
              <button data-testid={`management-team-email-edit-toggle-${manager.id}`} onClick={() => setShowEmailEdit(true)} title="Click to edit this manager's email address." className="ml-2 text-igc-goldText/70 hover:text-igc-goldText underline">edit</button>
            </p>
          )}
          {!manager.welcome_email_sent ? (
            <div className="flex items-center gap-2 mt-1">
              <p className="text-igc-muted text-[10px]" data-testid={`management-team-welcome-pending-${manager.id}`}>Welcome email not sent yet.</p>
              <button data-testid={`management-team-send-welcome-${manager.id}`} onClick={sendWelcomeEmail} title="Sends this manager their onboarding welcome email with login instructions." className="text-[10px] text-igc-goldText/80 hover:text-igc-goldText underline">Send Welcome Email</button>
            </div>
          ) : (
            <p className="text-igc-success text-[10px] mt-1" data-testid={`management-team-welcome-sent-${manager.id}`}>Welcome email sent ✓</p>
          )}
        </div>
        <span className={`text-[10px] px-2 py-1 uppercase tracking-wide border ${manager.payment_info_submitted ? "bg-igc-success/10 text-igc-success border-igc-success/30" : "bg-igc-muted/10 text-igc-muted border-igc-muted/30"}`} data-testid={`management-team-payment-status-${manager.id}`} title="Whether this manager has submitted their banking and tax info needed to receive commission payouts.">
          {manager.payment_info_submitted ? "Payment Info On File" : "Payment Info Missing"}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div><p className="text-igc-success text-sm" data-testid={`management-team-direct-${manager.id}`}>${manager.commission.direct_earned.toLocaleString()}</p><FieldLabel hint="Commission this manager earned directly on their own closed referrals.">Direct Earned</FieldLabel></div>
        <div><p className="text-igc-goldText text-sm" data-testid={`management-team-override-${manager.id}`}>${manager.commission.override_earned.toLocaleString()}</p><FieldLabel hint="Automatic earnings from this manager's team's closed deals. Calculated for you — no action needed.">Override Earned</FieldLabel></div>
        <div><p className="text-igc-cream text-sm" data-testid={`management-team-total-${manager.id}`}>${manager.commission.total_earned.toLocaleString()}</p><FieldLabel>Total Earned</FieldLabel></div>
        <div><p className="text-igc-cream text-sm" data-testid={`management-team-leads-${manager.id}`}>{manager.lead_count}</p><FieldLabel>Leads</FieldLabel></div>
        <div><p className="text-igc-cream text-sm" data-testid={`management-team-sub-count-${manager.id}`}>{manager.sub_affiliate_count}</p><FieldLabel>Reps</FieldLabel></div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 pt-3 border-t border-igc-gold/10">
        <div><p className="text-igc-cream text-sm" data-testid={`management-team-trust-leads-${manager.id}`}>{manager.team_trust_leads}</p><FieldLabel>Team Trust Leads</FieldLabel></div>
        <div><p className="text-igc-cream text-sm" data-testid={`management-team-funding-leads-${manager.id}`}>{manager.team_funding_leads}</p><FieldLabel>Team Funding Leads</FieldLabel></div>
        <div><p className="text-igc-cream text-sm" data-testid={`management-team-active-inactive-${manager.id}`}><span className="text-igc-success">{manager.team_reps_active_this_week}</span> / <span className="text-igc-muted">{manager.team_reps_inactive_this_week}</span></p><FieldLabel hint="Reps active vs. inactive this week (active = at least one call logged).">Active / Inactive</FieldLabel></div>
        <div><p className="text-igc-cream text-sm" data-testid={`management-team-calls-week-${manager.id}`}>{manager.team_calls_this_week}</p><FieldLabel>Team Calls This Week</FieldLabel></div>
      </div>
      <div className="mb-4">
        <p className="text-igc-cream text-sm" data-testid={`management-team-deals-submitted-${manager.id}`}>{manager.team_deals_submitted} <span className="text-igc-muted text-xs">team deals submitted</span></p>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <FieldLabel hint="How much of your team's earned commission this manager automatically gets. Enter a decimal, e.g. 0.10 = 10%." testId={`management-team-rate-hint-${manager.id}`}>Override Rate</FieldLabel>
        <Input
          data-testid={`management-team-rate-input-${manager.id}`}
          type="number" step="0.01" min="0" max="1"
          value={rateDraft}
          onChange={(e) => setRateDraft(e.target.value)}
          className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-28 h-9 text-xs"
        />
        <Button data-testid={`management-team-rate-save-${manager.id}`} onClick={saveRate} title="Saves the override rate you just typed for this manager." className="h-9 text-xs bg-transparent border border-igc-gold text-igc-goldText hover:bg-igc-gold hover:text-black">Save</Button>
        <button data-testid={`management-team-payment-toggle-${manager.id}`} onClick={togglePayment} title="Shows or hides this manager's banking and tax info on file (masked account/routing numbers, legal name, address)." className="text-xs px-3 h-9 border border-igc-gold/30 text-igc-muted hover:text-igc-goldText flex items-center gap-1">
          Payment & Tax Info {showPayment ? <CaretUp size={12} /> : <CaretDown size={12} />}
        </button>
        <button data-testid={`management-team-downline-toggle-${manager.id}`} onClick={toggleDownline} title="Shows or hides the list of reps reporting to this manager, along with each rep's lead/call/deal performance." className="text-xs px-3 h-9 border border-igc-gold/30 text-igc-muted hover:text-igc-goldText flex items-center gap-1">
          Reps {showDownline ? <CaretUp size={12} /> : <CaretDown size={12} />}
        </button>
        <button data-testid={`management-team-agreement-toggle-${manager.id}`} onClick={toggleAgreement} title="Shows or hides whether this manager has signed their affiliate agreement, and lets you download or email a copy." className="text-xs px-3 h-9 border border-igc-gold/30 text-igc-muted hover:text-igc-goldText flex items-center gap-1">
          Agreement {manager.agreement_signed ? <span className="text-igc-success">Signed ✓</span> : <span className="text-igc-danger">Unsigned</span>} {showAgreement ? <CaretUp size={12} /> : <CaretDown size={12} />}
        </button>
        <button data-testid={`management-team-signup-link-copy-${manager.id}`} onClick={copySignupLink} title="Copies this manager's personal signup referral link to your clipboard, so you can send it to new reps who should join under them." className="text-xs px-3 h-9 border border-igc-gold/30 text-igc-muted hover:text-igc-goldText flex items-center gap-1">
          <Copy size={12} /> Copy Signup Link
        </button>
      </div>

      {showPayment && paymentInfo && (
        <div className="bg-igc-surfaceLight border border-igc-gold/15 p-4 mb-3 text-xs text-igc-cream space-y-1" data-testid={`management-team-payment-panel-${manager.id}`}>
          <p>Legal name: {paymentInfo.legal_name}</p>
          <p>Bank: {paymentInfo.bank_name}</p>
          <p>ACH routing: {paymentInfo.ach_routing_last4}</p>
          <p>ACH account: {paymentInfo.ach_account_last4} ({paymentInfo.account_type})</p>
          <p>Tax classification: {paymentInfo.tax_classification} ({paymentInfo.tin_type}: {paymentInfo.tin_last4})</p>
          <p>Address: {paymentInfo.address}</p>
          <div className="flex items-center gap-2 pt-2">
            <button data-testid={`management-team-payment-download-${manager.id}`} onClick={downloadPaymentInfoPdf} title="Downloads this manager's payment and tax info as a PDF file to your device." className="text-[11px] px-2.5 py-1.5 border border-igc-gold/30 text-igc-goldText hover:bg-igc-gold/10 flex items-center gap-1">
              <DownloadSimple size={12} /> Download PDF
            </button>
            <button data-testid={`management-team-payment-email-${manager.id}`} onClick={emailPaymentInfoPdf} title="Emails a copy of this manager's payment and tax info PDF to their address on file." className="text-[11px] px-2.5 py-1.5 border border-igc-gold/30 text-igc-goldText hover:bg-igc-gold/10 flex items-center gap-1">
              <EnvelopeSimple size={12} /> Email Copy
            </button>
          </div>
        </div>
      )}

      {showDownline && (
        <div className="bg-igc-surfaceLight border border-igc-gold/15 p-4 text-xs text-igc-cream mb-3" data-testid={`management-team-downline-panel-${manager.id}`}>
          {manager.sub_affiliates.length === 0 ? (
            <p className="text-igc-muted">No reps assigned yet — assign via Admin &gt; Users.</p>
          ) : teamPerformance ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-igc-muted uppercase tracking-widest text-[10px] text-left">
                    <th className="py-1 pr-3">Rep</th>
                    <th className="py-1 pr-3">Assigned</th>
                    <th className="py-1 pr-3">Contacted</th>
                    <th className="py-1 pr-3">In Progress</th>
                    <th className="py-1 pr-3">Won</th>
                    <th className="py-1 pr-3">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {teamPerformance.map((r) => (
                    <tr key={r.affiliate_id} data-testid={`management-team-performance-row-${r.affiliate_id}`}>
                      <td className="py-1 pr-3">{r.affiliate_code} — {r.display_name}</td>
                      <td className="py-1 pr-3">{r.leads_assigned}</td>
                      <td className="py-1 pr-3">{r.leads_contacted}</td>
                      <td className="py-1 pr-3 text-igc-goldText">{r.deals_in_progress}</td>
                      <td className="py-1 pr-3 text-igc-success">{r.deals_won}</td>
                      <td className="py-1 pr-3 text-igc-success">${r.commission_earned.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="space-y-1">
              {manager.sub_affiliates.map((a) => (
                <p key={a.id}>{a.affiliate_code} — {a.display_name}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {showAgreement && agreement && (
        <div className="bg-igc-surfaceLight border border-igc-gold/15 p-4 text-xs text-igc-cream space-y-1" data-testid={`management-team-agreement-panel-${manager.id}`}>
          {agreement.signed ? (
            <>
              <p>Signed by: {agreement.full_legal_name}</p>
              <p>Contact: {agreement.email} / {agreement.phone}</p>
              <p>Signed on: {formatET(agreement.signed_at, "MMM d, yyyy h:mm a")}</p>
              <div className="flex items-center gap-2 pt-2">
                <button data-testid={`management-team-agreement-download-${manager.id}`} onClick={downloadAgreementPdf} title="Downloads this manager's signed affiliate agreement as a PDF to your device." className="text-[11px] px-2.5 py-1.5 border border-igc-gold/30 text-igc-goldText hover:bg-igc-gold/10 flex items-center gap-1">
                  <DownloadSimple size={12} /> Download PDF
                </button>
                <button data-testid={`management-team-agreement-email-${manager.id}`} onClick={emailAgreementPdf} title="Emails a copy of the signed agreement PDF to this manager's address on file." className="text-[11px] px-2.5 py-1.5 border border-igc-gold/30 text-igc-goldText hover:bg-igc-gold/10 flex items-center gap-1">
                  <EnvelopeSimple size={12} /> Email Copy
                </button>
              </div>
            </>
          ) : (
            <p className="text-igc-muted">Not signed yet.</p>
          )}
        </div>
      )}
    </div>
  );
}
